import axios from "axios";
import * as Sentry from "@sentry/vue";
import { getAPIUrlV2 } from "../apiConfig";
import { isCanceledAPIError, normalizeAPIError } from "@/services/apiErrors";

export const MAX_LIST_LIMIT = 1000;

// NL1: a page that comes back at exactly the requested limit was very likely
// truncated. Warn loudly (console + Sentry breadcrumb) so a tenant silently
// losing rows past the cap is noticed instead of assuming they have everything.
function warnIfListTruncated(entityName, result, limit) {
    if (!limit || !Array.isArray(result) || result.length < limit) return;
    const message =
        `[${entityName}] list returned ${result.length} rows, equal to the requested ` +
        `limit (${limit}) — results are likely truncated. Add pagination or raise the cap.`;
    console.error(message);
    Sentry.addBreadcrumb({
        category: "data",
        level: "warning",
        message,
        data: { entity: entityName, count: result.length, limit }
    });
}

export function querySuffix(params = {}) {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params || {})) {
        if (value === undefined || value === null || value === "") continue;
        query.set(key, value);
    }
    const serialized = query.toString();
    return serialized ? `?${serialized}` : "";
}

/**
 * Base class for API clients that provides common CRUD operations.
 * Eliminates repetitive Promise wrapper boilerplate across API classes.
 *
 * @example
 * class ProductAPI extends BaseAPI {
 *     constructor(emitterInstance) {
 *         super(emitterInstance, "products", "product");
 *     }
 * }
 */
export default class BaseAPI {
    emitter = null;
    route = null;
    entityName = null;

    /**
     * @param {Object} emitterInstance - Event emitter instance for publishing events
     * @param {string} route - API route path (e.g., "products", "users")
     * @param {string} entityName - Entity name for events (e.g., "product", "user")
     */
    constructor(emitterInstance, route, entityName) {
        this.emitter = emitterInstance;
        this.route = route;
        this.entityName = entityName;
    }

    /**
     * Core request method that handles axios calls with consistent response handling.
     *
     * @param {string} method - HTTP method (get, post, put, delete, patch)
     * @param {string} url - Full URL to request
     * @param {Object|null} [data=null] - Request body for POST/PUT/PATCH
     * @param {Object} [options={}] - Additional axios config options
     * @param {boolean} [options.fullResponse] - Return full axios response instead of data
     * @param {boolean} [options.rawData] - Return response.data instead of response.data.data
     * @param {string} [options.responseType] - Axios response type (e.g., "blob")
     * @param {Object} [options.headers] - Custom headers
     * @param {Function} [options.onUploadProgress] - Upload progress callback
     * @param {Function} [options.onDownloadProgress] - Download progress callback
     * @returns {Promise<any>}
     */
    async _request(method, url, data = null, options = {}) {
        const { fullResponse, rawData, ...axiosConfig } = options;

        try {
            const args = data !== null ? [url, data, axiosConfig] : [url, axiosConfig];
            const response = await axios[method](...args);

            if (fullResponse) return response;
            if (rawData) return response.data;
            return response.data.data;
        } catch (error) {
            if (isCanceledAPIError(error)) throw error;
            throw normalizeAPIError(error);
        }
    }

    /**
     * Emit an event with the standard naming convention.
     *
     * @param {string} action - Action name (e.g., "add", "update", "delete")
     * @param {string|null} [id=null] - Optional entity ID to include in event
     */
    _emit(action, id = null) {
        const eventName = `${this.entityName}-${action}`;
        this.emitter.emit(eventName, id);
    }

    /**
     * Build the full URL for this entity's API endpoint.
     *
     * @param {string} [suffix=""] - Optional path suffix (e.g., "/${id}", "?after=${after}")
     * @returns {string} Full URL
     */
    _url(suffix = "") {
        return `${getAPIUrlV2(this.route)}${suffix}`;
    }

    // ==================== Standard CRUD Methods ====================

    /**
     * Create a new entity.
     *
     * @param {Object} data - Entity data to create
     * @param {string} [idempotencyKey] - Idempotency-Key for endpoints
     *   protected by the backend's idempotency middleware. The caller owns
     *   the UUID; reuse on retry to dedup, generate fresh per user action.
     * @param {Object} [options] - Mutation behavior.
     * @param {boolean} [options.emitEvent=true] - Emit the local cache event.
     * @returns {Promise<Object>} Created entity
     */
    async Add(data, idempotencyKey = null, { emitEvent = true } = {}) {
        const options = idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {};
        const result = await this._request("post", this._url(), data, options);
        if (emitEvent) this._emit("add", result.id);
        return result;
    }

    /**
     * List all entities.
     *
     * @param {Object} [params={}] Query parameters.
     * @param {Object} [options={}] Additional axios request options.
     * @returns {Promise<Array>} Array of entities
     */
    async List(params = {}, options = {}) {
        const result = await this._request("get", this._url(querySuffix(params)), null, options);
        warnIfListTruncated(this.entityName, result, params.limit);
        // The backend serializes an empty result set as JSON `null` (a nil Go
        // slice), so honor the documented Array contract here — direct callers
        // spread/iterate the result and a `null` would throw.
        return result ?? [];
    }

    /**
     * List entities after a cursor (pagination).
     *
     * @param {string} after - Cursor/ID to paginate from
     * @returns {Promise<Array>} Array of entities
     */
    async ListAfter(after, params = {}) {
        const result = await this._request("get", this._url(querySuffix({ after, ...params })));
        return result ?? [];
    }

    /**
     * Get a single entity by ID.
     *
     * @param {string} id - Entity ID
     * @returns {Promise<Object>} Entity data
     */
    async GetByID(id) {
        return this._request("get", this._url(`/${id}`));
    }

    /**
     * Update an existing entity.
     *
     * @param {string} id - Entity ID
     * @param {Object} data - Updated entity data
     * @returns {Promise<Object>} Updated entity
     */
    async Update(id, data) {
        const result = await this._request("put", this._url(`/${id}`), data);
        this._emit("update", id);
        return result;
    }

    /**
     * Delete an entity.
     *
     * @param {string} id - Entity ID
     * @returns {Promise<Object>} Deletion result
     */
    async Delete(id) {
        const result = await this._request("delete", this._url(`/${id}`));
        this._emit("delete", id);
        return result;
    }
}
